/**
 * Sender Email Mappings Component
 * 
 * Allows super admins to configure which sender email addresses or domain
 * wildcards (*@domain.com) automatically map to which businesses for
 * incoming outgoing delivery emails
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config/api';

const isWildcardSender = (email) =>
  typeof email === 'string' && email.trim().toLowerCase().startsWith('*@');

export default function SenderEmailMappings({ businessId, businessName }) {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState({
    sender_email: '',
    sender_name: '',
    notes: '',
    is_active: true
  });

  const fetchMappings = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const endpoint = businessId 
        ? `${API_BASE_URL}/sender-email-mappings/business/${businessId}`
        : `${API_BASE_URL}/sender-email-mappings`;
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch sender email mappings');
      }
      
      const data = await response.json();
      setMappings(data.mappings || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching mappings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const handleOpenDialog = (mapping = null) => {
    if (mapping) {
      setEditingMapping(mapping);
      setFormData({
        sender_email: mapping.sender_email,
        sender_name: mapping.sender_name || '',
        notes: mapping.notes || '',
        is_active: mapping.is_active
      });
    } else {
      setEditingMapping(null);
      setFormData({
        sender_email: '',
        sender_name: '',
        notes: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMapping(null);
    setFormData({
      sender_email: '',
      sender_name: '',
      notes: '',
      is_active: true
    });
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const url = editingMapping
        ? `${API_BASE_URL}/sender-email-mappings/${editingMapping.mapping_id}`
        : `${API_BASE_URL}/sender-email-mappings`;
      
      const method = editingMapping ? 'PUT' : 'POST';
      
      const payload = editingMapping
        ? formData
        : { ...formData, business_id: businessId };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save mapping');
      }
      
      await fetchMappings();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving mapping:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this sender email mapping?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/sender-email-mappings/${mappingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete mapping');
      }
      
      await fetchMappings();
    } catch (err) {
      console.error('Error deleting mapping:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Incoming Email Mappings
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Configure which supplier email addresses or domains automatically route to this business
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Sender Email
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>How it works:</strong> When a delivery email arrives from a configured sender address,
          it will automatically be assigned to this business. Use a full address
          (e.g. <code>jameslewis@bandbgroup.co.uk</code>) or a domain wildcard
          (e.g. <code>*@bandbgroup.co.uk</code>) to match every sender from that domain.
          Exact addresses always take priority over wildcards.
        </Typography>
      </Alert>

      {mappings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Sender Email Mappings
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Add supplier email addresses or domain wildcards (*@domain.com) to automatically
            route their delivery emails to this business
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add First Sender Email
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sender Email</TableCell>
                <TableCell>Sender Name</TableCell>
                {!businessId && <TableCell>Business</TableCell>}
                <TableCell>Notes</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.map((mapping) => (
                <TableRow key={mapping.mapping_id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <EmailIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="body2" fontWeight="medium">
                        {mapping.sender_email}
                      </Typography>
                      {isWildcardSender(mapping.sender_email) && (
                        <Chip label="Domain" size="small" color="info" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{mapping.sender_name || '-'}</TableCell>
                  {!businessId && (
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <BusinessIcon sx={{ mr: 1, fontSize: 16 }} />
                        {mapping.business_name}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {mapping.notes || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={mapping.is_active ? 'Active' : 'Inactive'}
                      color={mapping.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(mapping.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(mapping)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(mapping.mapping_id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMapping ? 'Edit Sender Email Mapping' : 'Add Sender Email Mapping'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Sender Email or Domain Wildcard"
              type="text"
              value={formData.sender_email}
              onChange={(e) => setFormData({ ...formData, sender_email: e.target.value })}
              helperText="Exact: supplier@bandbgroup.co.uk — or all from domain: *@bandbgroup.co.uk"
              placeholder="*@bandbgroup.co.uk"
              required
              disabled={editingMapping !== null}
            />
            
            <TextField
              fullWidth
              label="Sender Name (Optional)"
              value={formData.sender_name}
              onChange={(e) => setFormData({ ...formData, sender_name: e.target.value })}
              helperText="e.g., B&B Group, Main Supplier"
            />
            
            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              helperText="Any additional information about this sender"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.sender_email}
          >
            {editingMapping ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

